import { useAuth } from "@/_core/hooks/useAuth";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { getLoginUrl } from "@/const";
import { useIsMobile } from "@/hooks/useMobile";
// TODO(convex-migration F4-5): trpc.meta.getConfig, trpc.autopilot.status e
// trpc.auth.updateAvatar ainda não foram portados pra Convex. Mantemos tRPC pra
// esses 3 hooks até existirem:
//   - api.meta.getConfig (query reativa)
//   - api.autopilot.status (query reativa — sem refetchInterval, polling some)
//   - api.storage.setAvatar (mutation; signature MUITO diferente — vai requerer
//     generateUploadUrl + PUT fetch + setAvatar(storageId), não base64)
import { trpc } from "@/lib/trpc";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import {
  LayoutDashboard,
  LogOut,
  PanelLeft,
  Megaphone,
  Settings,
  Brain,
  ExternalLink,
  Zap,
  Webhook,
  ShoppingCart,
  ArrowUpRight,
  Thermometer,
  Volume2,
  VolumeX,
  FileText,
  Users,
  Shield,
  Camera,
  Loader2,
} from "lucide-react";import { CSSProperties, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { DashboardLayoutSkeleton } from "./DashboardLayoutSkeleton";
import { Button } from "./ui/button";
import AccountSwitcher from "./AccountSwitcher";
import { useNotifications, NotificationBadge } from "./NotificationBadge";
import { useSaleNotification } from "@/hooks/useSaleNotification";
import AdminPanel from "./AdminPanel";
import { toast } from "sonner";

type MenuItem = {
  icon: any;
  label: string;
  path: string;
  isSpecial?: boolean;
  isCommand?: boolean;
  badgeKey?: "sales" | "command"; // key to look up in notification counts
};

const menuItems: MenuItem[] = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/" },
  { icon: Megaphone, label: "Campanhas", path: "/campaigns" },
  { icon: Thermometer, label: "Termômetro do Funil", path: "/funnel" },
  { icon: ShoppingCart, label: "Vendas", path: "/sales", badgeKey: "sales" },
  { icon: Users, label: "Leads", path: "/leads" },
  { icon: FileText, label: "Relatórios", path: "/reports" },
  { icon: Zap, label: "Central de Comando", path: "/command", isCommand: true, badgeKey: "command" },
  { icon: Brain, label: "Cérebro Central", path: "/chat", isSpecial: true },
  { icon: Webhook, label: "Integrações", path: "/integrations" },
  { icon: Settings, label: "Configurações", path: "/settings" },
];

const SIDEBAR_WIDTH_KEY = "sidebar-width";
const DEFAULT_WIDTH = 260;
const MIN_WIDTH = 200;
const MAX_WIDTH = 480;

// Mini radar SVG for sidebar when autopilot is active
function MiniRadar() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" className="shrink-0">
      <circle cx="9" cy="9" r="8" fill="none" stroke="rgba(16,185,129,0.3)" strokeWidth="1" strokeDasharray="2 2" />
      <circle cx="9" cy="9" r="5.5" fill="none" stroke="rgba(16,185,129,0.4)" strokeWidth="0.8" strokeDasharray="1.5 1.5" />
      <circle cx="9" cy="9" r="3" fill="none" stroke="rgba(16,185,129,0.5)" strokeWidth="0.6" />
      <circle cx="9" cy="9" r="1.2" fill="rgb(16,185,129)">
        <animate attributeName="opacity" values="1;0.4;1" dur="2s" repeatCount="indefinite" />
      </circle>
      <line x1="9" y1="9" x2="9" y2="1" stroke="rgb(16,185,129)" strokeWidth="1.2" strokeLinecap="round" opacity="0.8">
        <animateTransform attributeName="transform" type="rotate" from="0 9 9" to="360 9 9" dur="3s" repeatCount="indefinite" />
      </line>
      <circle cx="13" cy="5" r="0.8" fill="rgb(16,185,129)">
        <animate attributeName="opacity" values="0;1;0" dur="3s" begin="0.5s" repeatCount="indefinite" />
      </circle>
      <circle cx="5" cy="12" r="0.8" fill="rgb(16,185,129)">
        <animate attributeName="opacity" values="0;1;0" dur="3s" begin="1.5s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

// Shortcut to Meta Ads Manager for the connected account
function AdsManagerShortcut({ isCollapsed }: { isCollapsed: boolean }) {
  const configRaw = useQuery(api.meta.getConfig, {});
  const configQuery = { data: configRaw, isLoading: configRaw === undefined };
  const adAccountId = configQuery.data?.adAccountId;

  if (!adAccountId) return null;

  // Build Ads Manager URL - use the correct format with act_ prefix for the query param
  const accountNum = adAccountId.replace("act_", "");
  // Facebook Ads Manager expects the account number without "act_" prefix in the URL
  const adsManagerUrl = `https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${accountNum}&nav_source=no_referrer`;

  return (
    <a
      href={adsManagerUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-xs font-medium transition-all border border-blue-500/20 bg-blue-500/5 hover:bg-blue-500/15 hover:border-blue-500/40 text-blue-400 ${
        isCollapsed ? "justify-center px-0" : ""
      }`}
      title="Abrir Gerenciador de Anúncios Meta"
    >
      <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
        <path d="M6 8l4 3-4 3" />
        <line x1="12" y1="14" x2="16" y2="14" />
      </svg>
      {!isCollapsed && (
        <>
          <span className="truncate">Gerenciador de Anúncios</span>
          <ArrowUpRight className="h-3 w-3 ml-auto shrink-0 opacity-60" />
        </>
      )}
    </a>
  );
}

// Logo personalizada da conta de anúncio
function AccountLogo({ isCollapsed }: { isCollapsed: boolean }) {
  const configRaw = useQuery(api.meta.getConfig, {});
  const configQuery = { data: configRaw, isLoading: configRaw === undefined };
  const logoUrl = configQuery.data?.logoUrl;

  if (!logoUrl || isCollapsed) return null;

  // Detect if logo likely has white/light background by checking URL for hints
  // We'll always wrap in a nice container for safety
  return (
    <div className="mb-2 flex justify-center">
      <div className="relative rounded-xl overflow-hidden border border-border/30 bg-card/50 p-2 max-w-[180px] w-full flex items-center justify-center">
        <img
          src={logoUrl}
          alt="Logo da conta"
          className="max-h-10 max-w-full object-contain"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      </div>
    </div>
  );
}

// Toggle de notificação/som
const NOTIFICATION_SOUND_KEY = "meta-ads-brain-notification-sound";

function NotificationToggle({ isCollapsed }: { isCollapsed: boolean }) {
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem(NOTIFICATION_SOUND_KEY);
    return saved !== "false"; // default: enabled
  });

  const toggle = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem(NOTIFICATION_SOUND_KEY, String(newValue));
    // Dispatch custom event so useSaleNotification can react
    window.dispatchEvent(new CustomEvent("notification-sound-toggle", { detail: { enabled: newValue } }));
  };

  return (
    <button
      onClick={toggle}
      className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-xs font-medium transition-all border ${
        soundEnabled
          ? "border-emerald-500/20 bg-emerald-500/5 hover:bg-emerald-500/15 hover:border-emerald-500/40 text-emerald-400"
          : "border-red-500/20 bg-red-500/5 hover:bg-red-500/15 hover:border-red-500/40 text-red-400"
      } ${isCollapsed ? "justify-center px-0" : ""}`}
      title={soundEnabled ? "Notificações ativadas" : "Notificações silenciadas"}
    >
      {soundEnabled ? (
        <Volume2 className="h-4 w-4 shrink-0" />
      ) : (
        <VolumeX className="h-4 w-4 shrink-0" />
      )}
      {!isCollapsed && (
        <span className="truncate">{soundEnabled ? "Som ativado" : "Som desativado"}</span>
      )}
    </button>
  );
}

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  const { loading, user } = useAuth();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  if (loading) {
    return <DashboardLayoutSkeleton />;
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-8 p-8 max-w-md w-full">
          <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-3">
              <div>
                <img src="/assets/logo-manyflows.png" alt="Manyflows ADS AI" className="h-10" />
                <p className="text-xs text-muted-foreground mt-1">
                  Dashboard Inteligente de Tráfego
                </p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground text-center max-w-sm mt-2">
              Faça login para acessar seu dashboard de análise e otimização de
              campanhas Meta Ads.
            </p>
          </div>
          <Button
            onClick={() => {
              window.location.href = "/login";
            }}
            size="lg"
            className="w-full shadow-lg hover:shadow-xl transition-all"
          >
            Entrar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider
      style={
        {
          "--sidebar-width": `${sidebarWidth}px`,
        } as CSSProperties
      }
    >
      <DashboardLayoutContent setSidebarWidth={setSidebarWidth}>
        {children}
      </DashboardLayoutContent>
    </SidebarProvider>
  );
}

type DashboardLayoutContentProps = {
  children: React.ReactNode;
  setSidebarWidth: (width: number) => void;
};

function AvatarUploadItem() {
  // TODO(convex-migration F4-5): port to api.storage.setAvatar (signature ≠ base64;
  // novo flow: generateUploadUrl → PUT blob → setAvatar(storageId)). users.me é
  // reativo via useAuth, então o invalidate vai sumir naturalmente.
  const utils = trpc.useUtils();
  const uploadMutation = trpc.auth.updateAvatar.useMutation({
    onSuccess: () => {
      utils.auth.me.invalidate();
      toast.success("Foto atualizada!");
    },
    onError: (err: any) => toast.error("Erro ao enviar foto: " + err.message),
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Imagem deve ter no máximo 2MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(",")[1];
      uploadMutation.mutate({ base64, mimeType: file.type });
    };
    reader.readAsDataURL(file);
  };

  return (
    <DropdownMenuItem
      onClick={(e) => {
        e.preventDefault();
        fileInputRef.current?.click();
      }}
      className="cursor-pointer"
      disabled={uploadMutation.isPending}
    >
      {uploadMutation.isPending ? (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      ) : (
        <Camera className="mr-2 h-4 w-4" />
      )}
      <span>{uploadMutation.isPending ? "Enviando..." : "Alterar foto"}</span>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={handleFileChange}
      />
    </DropdownMenuItem>
  );
}

function DashboardLayoutContent({
  children,
  setSidebarWidth,
}: DashboardLayoutContentProps) {
  const { user, logout } = useAuth();
  const [location, setLocation] = useLocation();
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const activeMenuItem = menuItems.find((item) => item.path === location);
  const isMobile = useIsMobile();

  // Notification badges
  const { counts: notifCounts } = useNotifications();

  // Admin panel
  const [adminOpen, setAdminOpen] = useState(false);
  const isAdmin = user?.role === 'admin';

  // Sale notification sound + toast
  useSaleNotification();

  const autopilotStatusRaw = useQuery(api.autopilot.status, {});
  const autopilotStatus = { data: autopilotStatusRaw, isLoading: autopilotStatusRaw === undefined };
  const isAutopilotActive = autopilotStatus.data?.enabled ?? false;

  useEffect(() => {
    if (isCollapsed) setIsResizing(false);
  }, [isCollapsed]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const sidebarLeft =
        sidebarRef.current?.getBoundingClientRect().left ?? 0;
      const newWidth = e.clientX - sidebarLeft;
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
        setSidebarWidth(newWidth);
      }
    };
    const handleMouseUp = () => setIsResizing(false);

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, setSidebarWidth]);

  const renderMenuItem = (item: MenuItem) => {
    const isActive = location === item.path;
    const badgeCount = item.badgeKey ? (notifCounts as any)[item.badgeKey] || 0 : 0;

    // Special rendering for Cérebro Central
    if (item.isSpecial) {
      return (
        <SidebarMenuItem key={item.path}>
          <SidebarMenuButton
            isActive={isActive}
            onClick={() => setLocation(item.path)}
            tooltip={item.label}
            className={`h-10 transition-all font-medium ${
              isActive
                ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                : "text-emerald-400/70 hover:bg-emerald-500/10 hover:text-emerald-400"
            }`}
          >
            <div className="relative">
              <item.icon
                className={`h-4 w-4 ${
                  isActive ? "text-emerald-400" : "text-emerald-400/70"
                }`}
              />
              <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
            </div>
            <span>{item.label}</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    }

    // Special rendering for Central de Comando when autopilot is active
    if (item.isCommand && isAutopilotActive) {
      return (
        <SidebarMenuItem key={item.path}>
          <SidebarMenuButton
            isActive={isActive}
            onClick={() => setLocation(item.path)}
            tooltip={`${item.label} (Piloto Ativo)${badgeCount > 0 ? ` • ${badgeCount} novo(s)` : ""}`}
            className={`h-10 transition-all font-medium ${
              isActive
                ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 shadow-[0_0_12px_rgba(16,185,129,0.15)]"
                : "text-emerald-400/80 hover:bg-emerald-500/10 hover:text-emerald-400"
            }`}
          >
            <div className="relative">
              <MiniRadar />
              {badgeCount > 0 && <NotificationBadge count={badgeCount} />}
            </div>
            <span>{item.label}</span>
            {!isCollapsed && badgeCount <= 0 && (
              <span className="ml-auto text-[9px] font-bold uppercase tracking-wider text-emerald-400/80 bg-emerald-500/15 px-1.5 py-0.5 rounded">
                Auto
              </span>
            )}
            {!isCollapsed && badgeCount > 0 && (
              <span className="ml-auto flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold text-white bg-red-500 rounded-full shadow-lg shadow-red-500/30">
                {badgeCount > 99 ? "99+" : badgeCount}
              </span>
            )}
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    }

    // Default rendering with optional badge
    return (
      <SidebarMenuItem key={item.path}>
        <SidebarMenuButton
          isActive={isActive}
          onClick={() => setLocation(item.path)}
          tooltip={`${item.label}${badgeCount > 0 ? ` • ${badgeCount} novo(s)` : ""}`}
          className="h-10 transition-all font-normal"
        >
          <div className="relative">
            <item.icon
              className={`h-4 w-4 ${isActive ? "text-primary" : ""}`}
            />
            {isCollapsed && badgeCount > 0 && <NotificationBadge count={badgeCount} />}
          </div>
          <span>{item.label}</span>
          {!isCollapsed && badgeCount > 0 && (
            <span className="ml-auto flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold text-white bg-red-500 rounded-full shadow-lg shadow-red-500/30 animate-in zoom-in-50 duration-200">
              {badgeCount > 99 ? "99+" : badgeCount}
            </span>
          )}
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <>
      <div className="relative" ref={sidebarRef}>
        <Sidebar
          collapsible="icon"
          className="border-r-0"
          disableTransition={isResizing}
        >
          <SidebarHeader className="h-auto justify-center py-3">
            <div className="flex items-center gap-3 px-2 transition-all w-full">
              <button
                onClick={toggleSidebar}
                className="h-8 w-8 flex items-center justify-center hover:bg-accent rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring shrink-0"
                aria-label="Toggle navigation"
              >
                <PanelLeft className="h-4 w-4 text-muted-foreground" />
              </button>
              {!isCollapsed ? (
                <div className="flex items-center gap-2 min-w-0">
                  <img src="/assets/logo-manyflows.png" alt="Manyflows" className="h-7" />
                </div>
              ) : null}
            </div>
            {/* Account Switcher — below the logo */}
            {!isCollapsed && (
              <div className="px-2 mt-2">
                <AccountSwitcher />
              </div>
            )}
          </SidebarHeader>

          <SidebarContent className="gap-0">
            <SidebarMenu className="px-2 py-1">
              {menuItems.map(renderMenuItem)}
            </SidebarMenu>
          </SidebarContent>

          <SidebarFooter className="p-3">
            {/* Trial badge */}
            {!isCollapsed && (user as any)?.subscriptionStatus === "trial" && (user as any)?.trialDaysLeft > 0 && (
              <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2 mb-2">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
                  <span className="text-xs font-medium text-amber-400">
                    {(user as any).trialDaysLeft} dias restantes no trial
                  </span>
                </div>
                <div className="mt-1.5 h-1 rounded-full bg-amber-500/10 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all"
                    style={{ width: `${Math.max(5, ((user as any).trialDaysLeft / 14) * 100)}%` }}
                  />
                </div>
              </div>
            )}
            <AccountLogo isCollapsed={isCollapsed} />
            <AdsManagerShortcut isCollapsed={isCollapsed} />
            <NotificationToggle isCollapsed={isCollapsed} />
            <div className="my-1" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 rounded-lg px-1 py-1 hover:bg-accent/50 transition-colors w-full text-left group-data-[collapsible=icon]:justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <Avatar className="h-9 w-9 border shrink-0">
                    {(user as any)?.avatarUrl && <AvatarImage src={(user as any).avatarUrl} alt={user?.name || ""} />}
                    <AvatarFallback className="text-xs font-medium bg-primary/20 text-primary">
                      {user?.name?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
                    <p className="text-sm font-medium truncate leading-none">
                      {user?.name || "-"}
                    </p>
                    <p className="text-xs text-muted-foreground truncate mt-1.5">
                      {user?.email || "-"}
                    </p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <AvatarUploadItem />
                <DropdownMenuSeparator />
                {isAdmin && (
                  <>
                    <DropdownMenuItem
                      onClick={() => setAdminOpen(true)}
                      className="cursor-pointer"
                    >
                      <Shield className="mr-2 h-4 w-4 text-violet-400" />
                      <span>Painel Admin</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem
                  onClick={logout}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sair</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarFooter>
        </Sidebar>
        {isAdmin && <AdminPanel open={adminOpen} onOpenChange={setAdminOpen} />}
        <div
          className={`absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-primary/20 transition-colors ${isCollapsed ? "hidden" : ""}`}
          onMouseDown={() => {
            if (isCollapsed) return;
            setIsResizing(true);
          }}
          style={{ zIndex: 50 }}
        />
      </div>

      <SidebarInset>
        {isMobile && (
          <div className="flex border-b h-12 items-center justify-between bg-background/95 px-3 backdrop-blur supports-[backdrop-filter]:backdrop-blur sticky top-0 z-40">
            <div className="flex items-center gap-2">
              <SidebarTrigger className="h-8 w-8 rounded-lg" />
              <div className="flex items-center gap-2">
                <img src="/assets/logo-manyflows.png" alt="Manyflows" className="h-5" />
                <span className="text-sm font-semibold tracking-tight text-foreground">
                  {activeMenuItem?.label ?? "Menu"}
                </span>
              </div>
            </div>
            <AccountSwitcher collapsed />
          </div>
        )}
        <main className="flex-1 p-4 md:p-6">{children}</main>
      </SidebarInset>
    </>
  );
}
